import { z } from "zod";
import { jsonQuery } from "../navigation/queryCodecs";
import { useNavigationDateRange } from "components/scrapEyeDataDashboard/navigation/useNavigationDateRange";
import { NavigationQueryProvider } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { heatFlagsQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";

import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useMemo } from "react";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { HeatQualityFlagsPanel } from "components/heatQualityIssues/HeatQualityFlagsPanel";
import { FLAG_TRANSLATION_KEY } from "components/heatQualityIssues/issueLabels";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import type {
  HeatQualityFlag,
  HeatQualityIssue,
} from "src/store/api/generatedApi";
import { useGetHeatQualityIssuesQuery } from "src/store/api/generatedApi";
import DateTimeControls from "../components/DateTimeControls";

dayjs.extend(utc);

const paginationQuery = jsonQuery(
  z.object({
    page: z.number().int().nonnegative(),
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
  })
);
const issueOrderQuery = jsonQuery(
  z.array(
    z.object({
      field: z.enum(["heat_id", "tap_timestamp", "loading_start_time"]),
      sort: z.enum(["asc", "desc"]).nullable(),
    })
  )
);
const expandedHeatsQuery = jsonQuery(
  z.array(z.union([z.string(), z.number()]))
);

const FLAG_OPTIONS = Object.keys(FLAG_TRANSLATION_KEY) as HeatQualityFlag[];

const HeatIssuesPageContent = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { pickerValue, setPickerValue } = useNavigationDateRange(
    "issuesRange",
    () => [dayjs().subtract(7, "day"), dayjs()]
  );
  const [flagFilter, setFlagFilter] = useNavigationQuery(
    "issueFlags",
    [],
    heatFlagsQuery,
    "replace"
  );

  const [pagination, setPagination] = useNavigationQuery(
    "issuesPage",
    { page: 0, pageSize: 25 as const },
    paginationQuery,
    "replace"
  );
  const [order, setOrder] = useNavigationQuery(
    "issuesOrder",
    [],
    issueOrderQuery,
    "replace"
  );
  const [expandedHeats, setExpandedHeats] = useNavigationQuery(
    "issueHeats",
    [],
    expandedHeatsQuery,
    "push"
  );
  const { data, isFetching } = useGetHeatQualityIssuesQuery({
    tenantName,
    start: pickerValue[0]?.toISOString(),
    end: pickerValue[1]?.toISOString(),
  });

  const rows = useMemo(() => {
    const issues = data?.issues ?? [];
    if (flagFilter.length === 0) {
      return issues;
    }
    return issues.filter((issue) =>
      issue.flags.some((flag) => flagFilter.includes(flag.code))
    );
  }, [data, flagFilter]);

  const columns = useMemo<GridColDef<HeatQualityIssue>[]>(
    () => [
      { field: "heat_id", headerName: t("heat"), flex: 1, minWidth: 120 },
      {
        field: "tap_timestamp",
        headerName: t("tapTime"),
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) =>
          row.tap_timestamp != null
            ? dayjs.utc(row.tap_timestamp).format("YYYY-MM-DD HH:mm")
            : "",
      },
      {
        field: "loading_start_time",
        headerName: t("loadingStart"),
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) =>
          row.loading_start_time != null
            ? dayjs.utc(row.loading_start_time).format("YYYY-MM-DD HH:mm")
            : "",
      },
      {
        field: "issues",
        headerName: t("issues"),
        flex: 3,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexWrap: "wrap", gap: 0.5, py: 0.5 }}
          >
            {params.row.flags.map((flag) => (
              <Chip
                key={flag.code}
                label={t(FLAG_TRANSLATION_KEY[flag.code])}
                size="small"
                color="warning"
              />
            ))}
          </Stack>
        ),
      },
    ],
    [t]
  );

  // Expanding a heat reuses the shared panel, which renders each flag's
  // detail line (counts, thresholds, out-of-range masses).
  const getDetailPanelContent = useCallback(
    ({ row }: { row: HeatQualityIssue }) => (
      <Box sx={{ p: 1 }}>
        <HeatQualityFlagsPanel issues={[row]} />
      </Box>
    ),
    []
  );

  return (
    <Stack spacing={2} sx={{ p: 2, height: "100%" }}>
      <Typography variant="h1">{t("heatIssues")}</Typography>
      <Stack
        direction="row"
        spacing={2}
        sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
      >
        <Box sx={{ width: "fit-content", maxWidth: "100%" }}>
          <DateTimeControls
            pickerValue={pickerValue}
            onRangeChange={setPickerValue}
          />
        </Box>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={FLAG_OPTIONS}
          value={flagFilter}
          onChange={(_, value) => {
            setFlagFilter(value);
          }}
          getOptionLabel={(flag) => t(FLAG_TRANSLATION_KEY[flag])}
          sx={{ minWidth: 300, flex: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("filterByIssue")} />
          )}
        />
      </Stack>
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <HotkeyCompliantDataGrid<HeatQualityIssue>
          rows={rows}
          columns={columns}
          getRowId={(row) => row.heat_id}
          loading={isFetching}
          getRowHeight={() => "auto"}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={() => "auto"}
          disableRowSelectionOnClick
          pagination
          pageSizeOptions={[25, 50, 100]}
          paginationModel={pagination}
          onPaginationModelChange={(selection) => {
            if (
              selection.pageSize === 25 ||
              selection.pageSize === 50 ||
              selection.pageSize === 100
            )
              setPagination({
                page: selection.page,
                pageSize: selection.pageSize,
              });
          }}
          sortModel={order}
          onSortModelChange={(selection) => {
            const parsed = issueOrderQuery.parse(JSON.stringify(selection));
            if (parsed.valid) setOrder(parsed.value);
          }}
          detailPanelExpandedRowIds={new Set(expandedHeats)}
          onDetailPanelExpandedRowIdsChange={(selection) => {
            setExpandedHeats([...selection]);
          }}
        />
      </Box>
    </Stack>
  );
};

export const HeatIssuesPage = () => (
  <NavigationQueryProvider>
    <HeatIssuesPageContent />
  </NavigationQueryProvider>
);
